import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs'
import { SCurveChart } from '../components/charts/SCurveChart'
import { PlannedWeightForm } from '../components/forms/PlannedWeightForm'
import { ActualWeightForm } from '../components/forms/ActualWeightForm'
import { useCurvaS, useGenerateTimePeriods, useExportCurvaS, useImportCurvaS } from '../hooks/useCurvaS'

export const CurvaSPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const numProjectId = parseInt(projectId || '0')

  const {
    data: curvaData,
    isLoading,
    error,
    refetch
  } = useCurvaS(numProjectId)

  const generatePeriodsMutation = useGenerateTimePeriods(numProjectId)
  const exportMutation = useExportCurvaS(numProjectId)
  const importMutation = useImportCurvaS(numProjectId)
  const [activeTab, setActiveTab] = useState<'chart' | 'planned' | 'actual'>('chart')

  // Auto-generate periods if none exist
  useEffect(() => {
    if (curvaData && curvaData.aggregates.length === 0 && !generatePeriodsMutation.isPending) {
      generatePeriodsMutation.mutate({ type: 'week' })
    }
  }, [])

  if (!numProjectId) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded">
        <p className="text-red-700 font-semibold">Invalid project ID</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded">
        <p className="text-red-700 font-semibold">Error loading Curva S data</p>
        <p className="text-red-600 text-sm">{error.message}</p>
        <button
          onClick={() => refetch()}
          className="mt-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  if (isLoading || !curvaData) {
    return <div className="p-6 text-center text-gray-500">Loading Curva S data...</div>
  }

  // Calculate statistics
  const lastAggregate = curvaData.aggregates[curvaData.aggregates.length - 1]
  const projectProgress = lastAggregate?.plannedCumulativeWeight || 0
  const actualProgress = lastAggregate?.actualCumulativeWeight || 0
  const deviasi = lastAggregate?.deviation || null

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{curvaData.projectName}</h1>
          <p className="text-gray-600 mt-1">Kurva S Timeline Management</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => refetch()}
            className="btn-secondary"
          >
            <span className="mr-2">🔄</span> Refresh
          </button>
          <button
            onClick={() => exportMutation.mutate()}
            className="btn-secondary"
            disabled={exportMutation.isPending}
          >
            <span className="mr-2">{exportMutation.isPending ? '🔄' : '📥'}</span> {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
          </button>
          <label className="btn-secondary cursor-pointer">
            <span className="mr-2">📤</span> Import CSV
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  importMutation.mutate(file)
                }
              }}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-gray-600 text-sm">Period Type</p>
          <p className="text-2xl font-bold text-gray-900 capitalize">
            {curvaData.periodType}
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-gray-600 text-sm">Total Periods</p>
          <p className="text-2xl font-bold text-gray-900">{curvaData.totalPeriods}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-gray-600 text-sm">Project Progress (Plan)</p>
          <p className="text-2xl font-bold text-blue-600">{projectProgress.toFixed(1).replace('.', ',')}%</p>
        </div>

        <div className={`bg-white p-4 rounded-lg shadow`}>
          <p className="text-gray-600 text-sm">Project Progress (Actual)</p>
          <p className={`text-2xl font-bold ${actualProgress > 0 ? 'text-green-600' : 'text-gray-400'}`}>
            {actualProgress > 0 ? `${actualProgress.toFixed(1).replace('.', ',')}%` : '—'}
          </p>
          {deviasi !== null && (
            <p className={`text-sm mt-1 font-semibold ${deviasi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              Deviasi: {deviasi > 0 ? '+' : ''}{deviasi.toFixed(1).replace('.', ',')}%
            </p>
          )}
        </div>
      </div>

      {/* Alert jika belum ada data realisasi */}
      {actualProgress === 0 && curvaData.aggregates.length > 0 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-yellow-800 font-semibold">
            ⚠️ Belum ada data realisasi. Mulai input bobot realisasi di tab "Realisasi Aktual"
          </p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(val: any) => setActiveTab(val)}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="chart">📊 Chart</TabsTrigger>
          <TabsTrigger value="planned">📝 Bobot Rencana</TabsTrigger>
          <TabsTrigger value="actual">✅ Realisasi Aktual</TabsTrigger>
          <TabsTrigger value="summary">📋 Data Summary</TabsTrigger>
        </TabsList>

        {/* Chart Tab */}
        <TabsContent value="chart" className="bg-white p-4 rounded-lg shadow">
          <SCurveChart
            data={curvaData.aggregates}
            title={`Kurva S - ${curvaData.periodType === 'week' ? 'Weekly' : 'Daily'} Breakdown`}
            showDeviation={true}
            height={500}
          />
        </TabsContent>

        {/* Planned Weight Tab */}
        <TabsContent value="planned">
          <PlannedWeightForm projectId={numProjectId} />
        </TabsContent>

        {/* Actual Weight Tab */}
        <TabsContent value="actual">
          <ActualWeightForm projectId={numProjectId} />
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="bg-white p-4 rounded-lg shadow overflow-x-auto">
          <h3 className="text-lg font-semibold mb-4">Data Summary</h3>
          <table className="w-full text-sm text-center">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left">Periode</th>
                <th className="px-2 py-2">Rencana (%)</th>
                <th className="px-2 py-2">Realisasi (%)</th>
                <th className="px-2 py-2">Deviasi</th>
              </tr>
            </thead>
            <tbody>
              {curvaData.aggregates.map((agg, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="px-2 py-2 text-left font-semibold">{agg.periodLabel}</td>
                  <td className="px-2 py-2 text-blue-600">{agg.plannedCumulativeWeight.toFixed(2).replace('.', ',')}%</td>
                  <td className="px-2 py-2 text-green-600">
                    {agg.actualCumulativeWeight ? `${agg.actualCumulativeWeight.toFixed(2).replace('.', ',')}%` : '—'}
                  </td>
                  <td className={`px-2 py-2 font-semibold $
                    ${agg.deviation !== null
                      ? agg.deviation >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                      : 'text-gray-400'}
                  `}>
                    {agg.deviation !== null ? `${agg.deviation > 0 ? '+' : ''}${agg.deviation.toFixed(2).replace('.', ',')}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabsContent>
      </Tabs>
    </div>
  )
}